import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { cloneDeep, isEmpty } from "lodash";

import hrchyAclService from "@plannel/services/data/hrchy-acl-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import postnService from "@plannel/services/data/postn-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { gridPaste } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, DownloadButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "../utils/code-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";

const { snackBarMessageColor } = ColorUtil;
const HrchyPermission = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [customerHrchyAcl, setCustomerHrchyAcl] = useState([]);
  const custHrchySearch = useRef(reduxViewState?.custHrchySearch ??"");
  const itemHrchySearch = useRef(reduxViewState?.itemHrchySearch ?? "");
  const [positionData, setPositionData] = useState([]);
  const [customerHrchyData, setCustomerHrchyData] = useState([]);
  const [itemHrchyData, setItemHrchyData] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isBaseDataLoaded, setIsBaseDataLoaded] = useState(false);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (custHrchySearch.current) {
      searchFilters.custHrchyCd = custHrchySearch.current;
    }

    if (itemHrchySearch.current) {
      searchFilters.itemHrchyCd = itemHrchySearch.current;
    }


    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    return { ...params, searchFilters };
  }

  const retrievePosition = () => {
    return postnService.getAll()
      .then(response => {
        const results = response?.data?.results ?? [];
        setPositionData(results);
        return results;
      })
      .catch(e => {
        debug(e);
        setPositionData([]);
        return [];
      });
  }

  const retrieveCustomerHrchy = () => {
    return customerHrchyService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY, isAll: true }})
      .then(response => {
        const results = response?.data?.results ?? [];
        setCustomerHrchyData(results);
        return results;
      })
      .catch(e => {
        debug(e);
        setCustomerHrchyData([]);
        return [];
      });
  }

  const retriveItemHrchy = () => {
    return itemHrchyService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY, isAll: true }})
      .then(response => {
        const results = response?.data?.results ?? [];
        setItemHrchyData(results);
        return results;
      })
      .catch(e => {
        debug(e);
        setItemHrchyData([]);
        return [];
      });
  }

  const retrieveHrchyAcl = () => {
    const params = getRequestParams();

    const topCustHrchy = (customerHrchyData ?? []).filter(hrchy => hrchy?.parentHrchy === null)?.[0];
    const topItemHrchy = (itemHrchyData ?? []).filter(hrchy => hrchy?.parentHrchy === null)?.[0];

    hrchyAclService.getAll(params)
      .then(response => {
        const result = [];
        if (isEmpty(response.data.results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        for (const row of response.data.results) {
          if (row.nodePermsn) {
            row.ownerPermission = row.nodePermsn.toString().substring(0, 1);
            row.groupPermission = row.nodePermsn.toString().substring(1, 2);
            row.othersPermission = row.nodePermsn.toString().substring(2, 3);
          } else {
            row.ownerPermission = 0;
            row.groupPermission = 0;
            row.othersPermission = 0;
          }

          const rowData = {
            id: row.id,
            postnId: row.postn.id,
            customerHrchyCd: row.customerHrchy?.hrchyCd ?? topCustHrchy?.hrchyCd,
            customerHrchyName: row.customerHrchy?.name ?? topCustHrchy?.name,
            itemHrchyCd: row.itemHrchy?.hrchyCd ?? topItemHrchy?.hrchyCd,
            itemHrchyNm: row.itemHrchy?.name ?? topItemHrchy?.name,
            postnCd: row.postn.postnCd,
            postnName: row.postn.name,
            nodePermsn: row.nodePermsn,
            ownerPermission: row.ownerPermission,
            groupPermission: row.groupPermission,
            othersPermission: row.othersPermission,
            customerHrchy: row.customerHrchy ?? topCustHrchy,
            itemHrchy: row.itemHrchy ?? topItemHrchy,
            postn: row.postn,
            verNum: row.verNum,
            createdTs: row.createdTs,
            createdUser: row.createdUser,
            updatedTs: row.updatedTs,
            updatedUser: row.updatedUser
          };
          result.push(rowData);
        }
        setCustomerHrchyAcl(result);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {

        const reduxState = reduxUtil.createObjectState(viewName, {
          itemHrchySearch: itemHrchySearch.current,
          custHrchySearch: custHrchySearch.current,
          advancedFilters
        });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const saveHrchyAcl = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const topCustHrchy = customerHrchyData?.filter(hrchy => hrchy?.parentHrchy === null)?.[0];
    const topItemHrchy = itemHrchyData?.filter(hrchy => hrchy?.parentHrchy === null)?.[0];

    if (allStateData.length > 0) {
      for (const row of allStateData) {
        if (row.ownerPermission === "0" && row.groupPermission === "0" && row.othersPermission === "0") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmPermission") });
          retrieveHrchyAcl();
          return;
        }
        if (parseInt(row.groupPermission, 10) > parseInt(row.ownerPermission, 10)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmGroupPermission") });
          retrieveHrchyAcl();
          return;

        } else if (parseInt(row.othersPermission, 10) > parseInt(row.groupPermission, 10)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmOthersPermission") });
          retrieveHrchyAcl();
          return;
        }
        const nodePermsn = parseInt(row.ownerPermission + row.groupPermission + row.othersPermission, 10);
        row.nodePermsn = nodePermsn;

        if (row.customerHrchyCd === topCustHrchy?.hrchyCd) {
          row.customerHrchy = null;
          row.customerHrchyCd = null;
          row.customerHrchyName = null;
        }

        if (row.itemHrchyCd === topItemHrchy?.hrchyCd) {
          row.itemHrchy = null;
          row.itemHrchyCd = null;
          row.itemHrchyNm = null;
        }
      }

      hrchyAclService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveHrchyAcl();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      postnId: null,
      customerHrchyCd: null,
      customerHrchyName: null,
      itemHrchyCd: null,
      itemHrchyNm: null,
      postnName: null,
      nodePermsn: "600",
      ownerPermission: "6",
      groupPermission: "0",
      othersPermission: "0",
      customerHrchy: null,
      itemHrchy: null,
      postn: null,
      verNum: 0
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeItem = () => {
    const itemIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        itemIds.push(data.id)
      }
    );

    if (itemIds.length > 0) {
      hrchyAclService.remove(itemIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveHrchyAcl();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveHrchyAcl();
    } else if (action === "DELETE") {
      removeItem();
    }
  }

  const authorizationValueCellRenderer = (params) => {
    if (params) {
      const formatter = codeUtil.dpPermission.find(obj => {
        return obj.id === params.value
      });
      if (!formatter) {
        return "";
      }
      return GridUtils.gridValueL10N(formatter.auth);
    } 
    return "";
  }

  const customerHrchyCodeCellRenderer = (params) => {
    if (params) {
      const formatter = customerHrchyData.find(obj => {
        if (typeof(params.value) !== "string") {
          return obj === params.value
        } 
        return obj.hrchyCd === params.value
      });
      if (!formatter) {
        return "";
      }
      return formatter.hrchyCd;
    }
    return "";
  }

  const positionCodeCellRenderer = (params) => {
    if (params) {
      const formatter = positionData.find(obj => {
        if (typeof(params.value) !== "string") {
          return obj === params.value
        } 
        return obj?.postnCd === params?.value
      });
      return formatter?.postnCd;
    }
    return "";
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);
    const { data, column, node, newValue } = params;
    
    const rowData = data;
    if (column.colId === "groupPermission") {
      if (parseInt(newValue, 10) > parseInt(rowData.ownerPermission, 10)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmGroupPermission") });
      }
    } else if (column.colId === "othersPermission") {
      if (parseInt(newValue, 10) > parseInt(rowData.groupPermission, 10)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmOthersPermission") });
      }
    } else if (column.colId === "customerHrchyCd") {
      for (const customer of customerHrchyData) {
        if (customer.hrchyCd === newValue) {
          node.data.customerHrchyName = customer.name;
          node.data.customerHrchy = customer;
        }
      }
    } else if (column.colId === "itemHrchyCd") {
      for (const item of itemHrchyData) {
        if (item.hrchyCd === newValue) {
          node.data.itemHrchyName = item.name;
          node.data.itemHrchy = item;
        }
      }
    } else if (column.colId === "postnCd") {
      for (const postn of positionData) {
        if (postn.postnCd === newValue) {
          node.data.postnName = postn.name;
          node.data.postn = postn;
        }
      }
    }

    DataState.setDataState(params);
  }

  const columnDefs = [
    { headerName: "id", field: "id", type: ["defaultValueFormatter", "defaultValueParser"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "postnId", field: "postnId", type: ["defaultValueFormatter", "defaultValueParser"], hide: true, suppressColumnsToolPanel: true },
    { headerName: "customerHrchyCd", field: "customerHrchyCd", hide: false, cellClass: 'stringType',
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: customerHrchyCodeCellRenderer,
      width: 200,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: customerHrchyCodeCellRenderer,
        values: (customerHrchyData ?? []).map(obj => obj.hrchyCd)
      },
      context: { pasteOption: customerHrchyData },
      filterType: "string"
    },
    { headerName: "customerHrchyName", field: "customerHrchyName", hide: false, type: ["nonEditableColumn"], filterType: "string" },
    {
      headerName: "itemHrchyCd", field: "itemHrchyCd", hide: false, cellClass: 'stringType',
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: ({ value }) => {
        const matchedItemHrchy = (itemHrchyData ?? []).find(obj => obj.hrchyCd === value);
        return matchedItemHrchy?.hrchyCd || value;
      },
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: ({ value }) => {
          const matchedItemHrchy = (itemHrchyData ?? []).find(obj => obj.hrchyCd === value);
          return matchedItemHrchy?.hrchyCd || value;
        },
        values: (itemHrchyData ?? []).map(obj => obj.hrchyCd)
      },
      context: { pasteOption: itemHrchyData },
      filterType: "string"
    },
    { headerName: "itemHrchyNm", field: "itemHrchyNm", hide: false, type: ["nonEditableColumn"], filterType: "string" },
    { headerName: "postnCd", field: "postnCd", hide: false, cellClass: 'stringType',
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: positionCodeCellRenderer,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: positionCodeCellRenderer,
        values: (positionData ?? []).map(obj => obj.postnCd)
      },
      context: { pasteOption: positionData },
      filterType: "string",
    },
    { headerName: "postnName", field: "postnName", hide: false, type: ["nonEditableColumn"], filterType: "string" },
    {
      headerName: "ownerHrchyPermission", field: "ownerPermission",
      width: 150,
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: authorizationValueCellRenderer,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: authorizationValueCellRenderer,
        values: codeUtil.dpPermission.map(obj => obj.id)
      }, 
      filterType: "boolean",
      filterOption: codeUtil.dpPermission.map(obj => obj.auth)
    },
    {
      headerName: "subHrchyPermission", field: "groupPermission",
      width: 150,
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: authorizationValueCellRenderer,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: authorizationValueCellRenderer,
        values: codeUtil.dpPermission.map(obj => obj.id)
      }, 
      filterType: "boolean", 
      filterOption: codeUtil.dpPermission.map(obj => obj.auth)
    },
    {
      headerName: "othersHrchyPermission", field: "othersPermission",
      width: 150,
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: authorizationValueCellRenderer,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        valueFormatter: authorizationValueCellRenderer,
        values: codeUtil.dpPermission.map(obj => obj.id)
      }, 
      filterType: "boolean", 
      filterOption: codeUtil.dpPermission.map(obj => obj.auth)
    },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 11, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 11, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
  ];


  const getBaseDatas = () => {
    Promise.allSettled([
      retrievePosition(),
      retrieveCustomerHrchy(),
      retriveItemHrchy()
    ]).then(() => {
      setIsBaseDataLoaded(true);
    });
  }

  const onGridReady = (params) => {
    getBaseDatas();
    setLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const exportAllDataToExcel = () => {
    setOpen(false);
    
    const excelApi = cloneDeep(gridRef.current.api);
    excelApi.setGridOption('columnDefs', GridUtils.getColumnDefs(columnDefs));
    excelApi.exportDataAsExcel();
  }

  const exportAll = () => {
    setOpen(true);
    if (loading) {
      exportAllDataToExcel();
    } 
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    DataState.initialize(gridRef.current?.api);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(customerHrchyData) || !isEmpty(positionData) || !isEmpty(itemHrchyData)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHrchyData, positionData, itemHrchyData]);

  useEffect(() => {
    if (isBaseDataLoaded) {
      retrieveHrchyAcl();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, isBaseDataLoaded])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
      <TextFieldSearch sx={{mr: 1.5, width: 250 }} label={"customerHrchyCd"} searchCode={custHrchySearch} retrieve={retrieveHrchyAcl}/>
      <TextFieldSearch sx={{mr: 1.5, width: 250 }} label={"itemHrchyCd"} searchCode={itemHrchySearch} retrieve={retrieveHrchyAcl}/>
        <Box sx={{ ml: "auto" }}>
          <DownloadButton label="exportAll" onClick={exportAll} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ pb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={customerHrchyAcl}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default withTranslation()(HrchyPermission);